import { getAuth, signInAnonymously, onAuthStateChanged, Auth, User } from 'firebase/auth';

// Auth state
let auth: Auth | null = null;
let currentUser: User | null = null;
let authToken: string | null = null;
let isInitializing = false;
let isInitialized = false;

// Promise to track auth readiness
let authReadyPromise: Promise<void> | null = null;
let authReadyResolve: (() => void) | null = null;

// Initialize Firebase Auth
export const initAuth = async (firebaseApp: any) => {
  if (isInitialized || isInitializing) {
    return authReadyPromise;
  }
  
  // Create a promise that resolves when auth is ready
  authReadyPromise = new Promise((resolve) => {
    authReadyResolve = resolve;
  });
  
  isInitializing = true;
  try {
    console.log('Initializing auth...');
    auth = getAuth(firebaseApp);
    
    // Set up auth state listener
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        currentUser = user;
        // Get the ID token for API authentication
        authToken = await user.getIdToken();
        console.log('✅ User authenticated:', user.uid);
        console.log('✅ Auth token received, length:', authToken?.length);
        
        // Resolve the auth ready promise
        if (authReadyResolve) {
          authReadyResolve();
          authReadyResolve = null;
        }
      } else {
        currentUser = null;
        authToken = null;
        console.log('User signed out');
      }
    });
    
    // Automatically sign in anonymously
    await signInAnonymously(auth);
    isInitialized = true;
    console.log('Auth initialized successfully');
  } catch (error: any) {
    console.error('Auth init error:', error);
    
    // Resolve auth ready promise even on error to prevent hanging
    if (authReadyResolve) {
      authReadyResolve();
      authReadyResolve = null;
    }
  } finally {
    isInitializing = false;
  }
  
  return authReadyPromise;
};

// Wait for auth to be ready
export const waitForAuth = async (): Promise<void> => {
  // If auth is already initialized and has a token, return immediately
  if (isInitialized && currentUser && authToken) {
    return;
  }
  
  // If auth is initializing, wait for the promise
  if (authReadyPromise) {
    await authReadyPromise;
    return;
  }
  
  // If auth hasn't started initializing yet, wait for it to start
  // Poll every 100ms until authReadyPromise is created (max 5 seconds)
  const maxWaitTime = 5000; // 5 seconds
  const pollInterval = 100; // 100ms
  const startTime = Date.now();
  
  while (!authReadyPromise && !isInitialized) {
    if (Date.now() - startTime > maxWaitTime) {
      console.warn('Auth initialization timeout - auth may not be initializing');
      return;
    }
    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }
  
  // Now wait for the auth ready promise if it exists
  if (authReadyPromise) {
    await authReadyPromise;
  }
};

// Check if auth is ready and has a token
export const isAuthReady = (): boolean => {
  return isInitialized && currentUser !== null && authToken !== null;
};

// Get current auth token - REQUIRED for API calls
export const getAuthToken = async (): Promise<string | null> => {
  // Wait for auth to be ready first
  await waitForAuth();
  
  if (!currentUser) {
    console.warn('No authenticated user - auth may not be initialized yet');
    return null;
  }
  
  try {
    // Get fresh token (will use cached token if still valid)
    authToken = await currentUser.getIdToken();
    
    if (!authToken) {
      console.warn('Failed to get auth token from authenticated user');
      return null;
    }
    
    return authToken;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

// Get current user
export const getCurrentUser = (): User | null => {
  return currentUser;
};

// Get current user ID (from Firebase auth)
export const getCurrentUserId = (): string | null => {
  return currentUser?.uid || null;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return currentUser !== null;
};

// Read Firebase auth data from IndexedDB (for debugging/inspection)
export const getFirebaseAuthFromIndexedDB = async (): Promise<any> => {
  try {
    // Firebase stores auth data in IndexedDB under firebaseLocalStorageDb
    const dbName = 'firebaseLocalStorageDb';
    const storeName = 'firebaseLocalStorage';
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName);
      
      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const getAllRequest = store.getAll();
        
        getAllRequest.onsuccess = () => {
          const allData = getAllRequest.result;
          console.log('Firebase IndexedDB data:', allData);
          
          // Find auth user data
          const authData = allData.find((item: any) => 
            item.key && item.key.includes('authUser')
          );
          
          if (authData) {
            try {
              const parsed = JSON.parse(authData.value);
              console.log('Firebase Auth User from IndexedDB:', parsed);
              resolve(parsed);
            } catch (e) {
              resolve(authData.value);
            }
          } else {
            resolve(null);
          }
        };
        
        getAllRequest.onerror = () => reject(getAllRequest.error);
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error reading Firebase auth from IndexedDB:', error);
    return null;
  }
};

// Sign out (if needed for testing/debugging)
export const signOut = async (): Promise<void> => {
  if (!auth) return;
  
  try {
    await auth.signOut();
    console.log('User signed out successfully');
  } catch (error) {
    console.error('Sign out error:', error);
  }
};


